// Shared helpers for money / decimal inputs across the app.
// Reused by salary, subscriptions, the public real-wage calculator, and admin.
// Do NOT create parallel implementations — extend these instead.

/**
 * Props to spread onto a money / decimal `<input>`.
 * - `type="text"` so a Norwegian user can type `99,90` without the browser
 *   silently clearing it (which native `type="number"` does).
 * - `inputMode="decimal"` exposes the mobile numeric keypad with a
 *   separator key (unlike `"numeric"` which is digits-only).
 */
export const moneyInputProps = {
  type: "text" as const,
  inputMode: "decimal" as const,
  autoComplete: "off",
};

/**
 * Sanitises raw input from a money / decimal field:
 * - keeps digits, spaces, NBSP, one decimal separator, and a leading `-`
 * - collapses multiple separators down to the first one
 * The input remains display-friendly (grouped like `650 000`) — parsing to
 * a number is a separate step (see `parseMoneyInput`).
 */
export function sanitizeMoney(raw: string): string {
  let s = raw.replace(/[^\d\s\u00A0.,-]/g, "");
  const firstSep = s.search(/[.,]/);
  if (firstSep !== -1) {
    const before = s.slice(0, firstSep + 1);
    const after = s.slice(firstSep + 1).replace(/[.,]/g, "");
    s = before + after;
  }
  return s;
}

/**
 * Parses a sanitised money string into a finite number, or `null` for empty
 * input. Returns `NaN` for a non-numeric string so callers (or Zod
 * validators) can reject it explicitly.
 *
 * Strips regular / non-breaking / narrow-NBSP spaces and treats `,` as a
 * decimal separator — matching the normalisation used by the salary Zod
 * schema (`normalizeMoney`) and the shared display formatters.
 */
export function parseMoneyInput(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : NaN;
  const stripped = v.replace(/[\s\u00A0\u202F]/g, "").replace(",", ".");
  if (stripped === "") return null;
  const n = Number(stripped);
  return Number.isFinite(n) ? n : NaN;
}

/**
 * Zod-friendly preprocessor for money strings.
 * - empty / whitespace-only → `null`
 * - numeric strings → number
 * - non-numeric strings → returned as-is so Zod fails with
 *   `invalid_type_error` (a clear localised message) rather than silently
 *   coercing to `NaN`.
 */
export function normalizeMoneyForZod(v: unknown): unknown {
  if (v === null || v === undefined) return v;
  if (typeof v === "number") return v;
  if (typeof v !== "string") return v;
  const stripped = v.replace(/[\s\u00A0\u202F]/g, "").replace(",", ".");
  if (stripped === "") return null;
  const n = Number(stripped);
  return Number.isFinite(n) ? n : v;
}
